import { cva } from 'class-variance-authority'

/**
 * Shared component looks, as variants rather than as class strings copied between files.
 *
 * Ported from the sibling project (manapulse) so the two stay recognisably one house style, with the
 * palette swapped for this site's tokens — `ink`/`surface`/`line` here against `text`/`surface-2`/
 * `border` there. The structure is deliberately unchanged: same variant names, same states, same
 * reliance on Ark's `data-[selected]` rather than a React-held "isActive" class.
 */
export const tabList = cva('inline-flex', {
  variants: {
    variant: {
      segmented: 'overflow-hidden rounded-lg border border-line text-sm',
      underline: 'gap-1 border-b border-line',
    },
  },
  defaultVariants: { variant: 'segmented' },
})

export const tabTrigger = cva(
  'cursor-pointer whitespace-nowrap font-medium transition-colors ' +
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
  {
    variants: {
      variant: {
        segmented:
          'inline-flex items-center gap-1.5 border-l border-line bg-surface px-4 py-1.5 text-sm ' +
          'text-ink-soft first:border-l-0 hover:bg-surface-raised hover:text-ink ' +
          'data-[selected]:bg-accent-bg data-[selected]:text-accent',
        underline:
          '-mb-px flex items-center gap-1.5 border-b-2 border-transparent px-3 py-2 text-sm ' +
          'text-ink-soft hover:text-ink ' +
          'data-[selected]:border-accent data-[selected]:text-accent',
      },
    },
    defaultVariants: { variant: 'segmented' },
  },
)

/**
 * Buttons that do something, as opposed to buttons that navigate.
 *
 * New with fantasy betting, because until then the site had nothing to press: every page was a read,
 * and the only controls were tabs and disclosures. Placing a bet, joining a season and minting an
 * invite are the first real actions here, so they get one definition rather than a class string
 * copied into five files — the same reasoning that put the tab looks above.
 *
 * `primary` carries the accent surface with an edge that solidifies on hover, which is the
 * vocabulary the rest of the set already speaks: `Detail` firms a dotted rule the same way and a
 * card firms its border. Deliberately not a filled accent block — nothing else on this site is one,
 * and a single loud button would read as belonging to a different product.
 *
 * The 44px floor is Apple's minimum comfortable target and relaxes once there is a pointer. These
 * are controls a reader hits with a thumb, minutes before a match starts.
 */
export const actionButton = cva(
  'inline-flex min-h-11 shrink-0 items-center justify-center gap-1.5 rounded px-3 text-sm ' +
    'transition-colors disabled:cursor-not-allowed disabled:opacity-50 ' +
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent sm:min-h-9',
  {
    variants: {
      variant: {
        primary:
          'border border-accent-edge bg-accent-bg font-medium text-accent hover:border-accent',
        quiet: 'text-ink-soft hover:bg-surface-raised hover:text-ink',
        danger: 'text-ink-soft hover:bg-danger-bg hover:text-danger',
      },
    },
    defaultVariants: { variant: 'primary' },
  },
)
